import { useState } from "react";
import { useFetcher } from "react-router";
import type { Member, Tenant } from "@repo/shared-types";
import { GRANTS, type Powers } from "@/lib/grants";
import { ConfirmDialog, Modal, type Outcome } from "@/components/modal";
import { ErrorNote, SegmentedControl, ToggleChips } from "@/components/form";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

/**
 * One member, as whoever may manage them sees them (decided with Tiago 12
 * October): their role, saved the moment it is tapped; an admin's
 * permissions, each chip saving the whole set; then handing the space on and
 * removing them, each asked first. Only what the viewer may do is drawn —
 * `powersOver` decides, and the api refuses the rest however the page
 * renders.
 *
 * Each save posts to the members page's own action by fetcher, which toasts
 * what worked; the page's loaders then read the list again, and this sheet
 * draws from it. Handing on or removing leaves the viewer nothing to do to
 * this member, so the page closes the sheet when the list says so.
 */
export function MemberSheet({
  tenant,
  member,
  powers,
  onClose,
}: {
  tenant: Tenant;
  member: Member;
  powers: Powers;
  onClose: () => void;
}) {
  const [asking, setAsking] = useState<"hand-on" | "remove" | null>(null);
  const name = member.name || member.username;

  // The question takes the sheet's place, and Cancel gives it back.
  if (asking === "hand-on") {
    return (
      <ConfirmDialog
        title={`Hand ${tenant.name} on to ${name}?`}
        fields={{ intent: "set-role", user: member.userId, role: "owner" }}
        confirm="Hand it on"
        onClose={() => setAsking(null)}
      >
        <p>
          {name} becomes its owner. You stay in it as an admin holding no permissions: its settings, who
          is an admin and deleting it become {name}'s alone, and only {name} can hand it back.
        </p>
      </ConfirmDialog>
    );
  }
  if (asking === "remove") {
    return (
      <ConfirmDialog
        title={`Remove ${name} from ${tenant.name}?`}
        fields={{ intent: "remove", user: member.userId }}
        confirm="Remove"
        onClose={() => setAsking(null)}
      >
        <p>
          They stop seeing its events, leaderboards and conversation. Their results stay in their own log,
          and anything they shared here stays on its boards. To come back they will need a new invitation
          {tenant.visibility === "public" ? ", or to ask to join again" : ""}.
        </p>
      </ConfirmDialog>
    );
  }

  return (
    <Modal title={name} onClose={onClose}>
      <p className="-mt-3 truncate text-sm text-muted-foreground">@{member.username}</p>
      {powers.role && <RoleControl member={member} />}
      {powers.grants && <PermissionChips member={member} />}
      {(powers.handOn || powers.remove) && (
        <>
          {(powers.role || powers.grants) && <Separator />}
          <div className="space-y-2">
            {powers.handOn && (
              <Button type="button" variant="outline" size="lg" className="w-full" onClick={() => setAsking("hand-on")}>
                Hand the space on…
              </Button>
            )}
            {powers.remove && (
              <Button type="button" variant="outline" size="lg" className="w-full" onClick={() => setAsking("remove")}>
                Remove from the space…
              </Button>
            )}
          </div>
        </>
      )}
      <Button type="button" size="lg" className="w-full" onClick={onClose}>
        Done
      </Button>
    </Modal>
  );
}

type Role = "member" | "admin";

/**
 * Member or Admin, saved on the tap. While it is on its way the tapped one
 * shows; refused, the role the list holds comes back, with why beneath.
 */
function RoleControl({ member }: { member: Member }) {
  const fetcher = useFetcher<Outcome>();
  const pending = fetcher.formData?.get("role");
  const role = (typeof pending === "string" ? pending : member.role) as Role;
  return (
    <div className="space-y-2">
      <SegmentedControl<Role>
        label="Role"
        name="role"
        options={[
          { value: "member", label: "Member" },
          { value: "admin", label: "Admin" },
        ]}
        value={role}
        onChange={(next) => {
          if (next !== role) fetcher.submit({ intent: "set-role", user: member.userId, role: next }, { method: "post" });
        }}
      />
      {fetcher.data?.error && <ErrorNote message={fetcher.data.error} />}
    </div>
  );
}

/**
 * An admin's permissions, a chip each, every tap sending the whole set — so
 * a tap made while the last is on its way still sends what the chips show,
 * and the last one sent is what they hold. A grant this build does not know
 * is kept, never dropped: the set grows by addition.
 */
function PermissionChips({ member }: { member: Member }) {
  const fetcher = useFetcher<Outcome>();
  const held = fetcher.formData ? fetcher.formData.getAll("grants").map(String) : member.grants;

  const save = (grants: string[]) => {
    const form = new FormData();
    form.set("intent", "grants");
    form.set("user", member.userId);
    for (const grant of grants) form.append("grants", grant);
    fetcher.submit(form, { method: "post" });
  };

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-foreground">Permissions</p>
      <ToggleChips
        label="Permissions"
        options={GRANTS.map((grant) => ({
          name: grant.name,
          label: grant.label,
          says: `Can ${grant.description}`,
          on: held.includes(grant.name),
          onChange: (on: boolean) =>
            save(on ? [...held.filter((name) => name !== grant.name), grant.name] : held.filter((name) => name !== grant.name)),
        }))}
      />
      {held.length === 0 && (
        <p className="text-xs text-muted-foreground">A new admin holds nothing until you give them a permission.</p>
      )}
      {fetcher.data?.error && <ErrorNote message={fetcher.data.error} />}
    </div>
  );
}

const ROLES: Record<string, { label: string; variant: "default" | "secondary" | "outline" }> = {
  owner: { label: "Owner", variant: "default" },
  admin: { label: "Admin", variant: "secondary" },
  member: { label: "Member", variant: "outline" },
};

/** A member's role, as the space's header badges draw the viewer's own. */
export function RoleBadge({ role }: { role: string }) {
  const known = ROLES[role] ?? { label: role, variant: "outline" as const };
  return <Badge variant={known.variant}>{known.label}</Badge>;
}
